import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import type { ApiResponse } from '@/types'
import { adminApiClient } from '@/utils/apiClient'

export interface AdminCacheServiceConfigSnapshot {
  ttl: number
  enabled: boolean
  description: string
}

export interface AdminCacheConfigSnapshot {
  merchant: AdminCacheServiceConfigSnapshot
  order: AdminCacheServiceConfigSnapshot
  user: AdminCacheServiceConfigSnapshot
  global: {
    kvNamespace: 'CACHE' | 'CACHE_TESTNET'
    keyPrefix: ''
  }
}

export interface AdminCacheConfigResponse {
  mode: NetworkEnvironment
  environment: string
  config: AdminCacheConfigSnapshot
  timestamp: string
}

export interface AdminCacheDebugResponse extends AdminCacheConfigResponse {
  bindings: {
    businessCache: {
      name: 'CACHE' | 'CACHE_TESTNET'
      configured: boolean
    }
    adminCache: {
      name: 'ADMIN_CACHE' | 'ADMIN_CACHE_TESTNET'
      configured: boolean
    }
  }
}

export interface CacheKeyEntry {
  name: string
  expiration?: number
  metadata?: unknown
}

export interface CacheKeyListResponse {
  success: true
  data: {
    keys: CacheKeyEntry[]
    cursor: string | null
    complete: boolean
    count: number
    prefix: string
  }
}

const CONFIG_DESCRIPTIONS = {
  merchant: 'Caches merchant ID mappings and basic merchant data',
  order: 'Caches order details for quick access',
  user: 'Caches user authentication and profile data',
} as const

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const requireExactRecord = (
  value: unknown,
  keys: readonly string[],
  field: string
): Record<string, unknown> => {
  if (!isRecord(value)) {
    throw new Error(`${field} must be an object`)
  }
  const actualKeys = Object.keys(value)
  if (
    actualKeys.length !== keys.length ||
    keys.some(key => !Object.prototype.hasOwnProperty.call(value, key))
  ) {
    throw new Error(`${field} has an invalid shape`)
  }
  return value
}

const requireBoolean = (value: unknown, field: string): boolean => {
  if (typeof value !== 'boolean') {
    throw new Error(`${field} must be a boolean`)
  }
  return value
}

const requireNonEmptyString = (value: unknown, field: string): string => {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`${field} must be a non-empty string`)
  }
  return value
}

const requirePositiveSafeInteger = (value: unknown, field: string): number => {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value <= 0) {
    throw new Error(`${field} must be a positive safe integer`)
  }
  return value
}

const requireCanonicalIso = (value: unknown, field: string): string => {
  if (typeof value !== 'string') {
    throw new Error(`${field} must be a canonical UTC timestamp`)
  }
  const epochMs = Date.parse(value)
  if (!Number.isFinite(epochMs) || new Date(epochMs).toISOString() !== value) {
    throw new Error(`${field} must be a canonical UTC timestamp`)
  }
  return value
}

const parseConfigService = (
  value: unknown,
  service: keyof typeof CONFIG_DESCRIPTIONS
): AdminCacheServiceConfigSnapshot => {
  const field = `cache config ${service}`
  const record = requireExactRecord(
    value,
    ['ttl', 'enabled', 'description'],
    field
  )
  const description = requireNonEmptyString(
    record.description,
    `${field}.description`
  )
  if (description !== CONFIG_DESCRIPTIONS[service]) {
    throw new Error(`${field} description is invalid`)
  }
  return {
    ttl: requirePositiveSafeInteger(record.ttl, `${field}.ttl`),
    enabled: requireBoolean(record.enabled, `${field}.enabled`),
    description,
  }
}

const parseConfigFields = (
  record: Record<string, unknown>,
  expectedMode?: NetworkEnvironment
): AdminCacheConfigResponse => {
  if (record.mode !== 'mainnet' && record.mode !== 'testnet') {
    throw new Error('Cache config mode is invalid')
  }
  if (expectedMode && record.mode !== expectedMode) {
    throw new Error('Cache config mode does not match the selected network')
  }
  const environment = requireNonEmptyString(
    record.environment,
    'cache config environment'
  )
  if (environment !== environment.trim()) {
    throw new Error('Cache config environment must be trimmed')
  }
  const config = requireExactRecord(
    record.config,
    ['merchant', 'order', 'user', 'global'],
    'cache config'
  )
  const global = requireExactRecord(
    config.global,
    ['kvNamespace', 'keyPrefix'],
    'cache config global'
  )
  const expectedNamespace = record.mode === 'testnet' ? 'CACHE_TESTNET' : 'CACHE'
  if (global.kvNamespace !== expectedNamespace || global.keyPrefix !== '') {
    throw new Error('Cache config global binding is invalid')
  }
  return {
    mode: record.mode,
    environment,
    config: {
      merchant: parseConfigService(config.merchant, 'merchant'),
      order: parseConfigService(config.order, 'order'),
      user: parseConfigService(config.user, 'user'),
      global: {
        kvNamespace: expectedNamespace,
        keyPrefix: '',
      },
    },
    timestamp: requireCanonicalIso(record.timestamp, 'cache config timestamp'),
  }
}

export const requireCacheConfig = (
  value: unknown,
  expectedMode?: NetworkEnvironment
): AdminCacheConfigResponse => {
  const record = requireExactRecord(
    value,
    ['mode', 'environment', 'config', 'timestamp'],
    'cache config response'
  )
  return parseConfigFields(record, expectedMode)
}

const parseBinding = <Name extends string>(
  value: unknown,
  expectedName: Name,
  field: string
): { name: Name; configured: boolean } => {
  const binding = requireExactRecord(value, ['name', 'configured'], field)
  if (binding.name !== expectedName) {
    throw new Error(`${field}.name is invalid`)
  }
  return {
    name: expectedName,
    configured: requireBoolean(binding.configured, `${field}.configured`),
  }
}

export const requireCacheDebugInfo = (
  value: unknown,
  expectedMode?: NetworkEnvironment
): AdminCacheDebugResponse => {
  const record = requireExactRecord(
    value,
    ['mode', 'environment', 'config', 'bindings', 'timestamp'],
    'cache debug response'
  )
  const config = parseConfigFields(record, expectedMode)
  const bindings = requireExactRecord(
    record.bindings,
    ['businessCache', 'adminCache'],
    'cache debug bindings'
  )
  const businessName = config.mode === 'testnet' ? 'CACHE_TESTNET' : 'CACHE'
  const adminName = config.mode === 'testnet' ? 'ADMIN_CACHE_TESTNET' : 'ADMIN_CACHE'
  return {
    ...config,
    bindings: {
      businessCache: parseBinding(
        bindings.businessCache,
        businessName,
        'cache debug businessCache'
      ),
      adminCache: parseBinding(
        bindings.adminCache,
        adminName,
        'cache debug adminCache'
      ),
    },
  }
}

export const requireCacheKeyList = (
  value: unknown,
  expectedPrefix: string
): CacheKeyListResponse => {
  const response = requireExactRecord(value, ['success', 'data'], 'cache keys response')
  if (response.success !== true) {
    throw new Error('cache keys response success flag is invalid')
  }
  const data = requireExactRecord(
    response.data,
    ['keys', 'cursor', 'complete', 'count', 'prefix'],
    'cache keys data'
  )
  if (!Array.isArray(data.keys)) {
    throw new Error('cache keys data.keys must be an array')
  }
  const keys = data.keys.map((valueToParse, index): CacheKeyEntry => {
    if (!isRecord(valueToParse)) {
      throw new Error(`cache keys data.keys[${index}] must be an object`)
    }
    const name = requireNonEmptyString(
      valueToParse.name,
      `cache keys data.keys[${index}].name`
    )
    const expiration = valueToParse.expiration
    if (
      expiration !== undefined &&
      (typeof expiration !== 'number' ||
        !Number.isSafeInteger(expiration) ||
        expiration <= 0)
    ) {
      throw new Error(`cache keys data.keys[${index}].expiration is invalid`)
    }
    return {
      name,
      ...(expiration === undefined ? {} : { expiration }),
      ...(Object.prototype.hasOwnProperty.call(valueToParse, 'metadata')
        ? { metadata: valueToParse.metadata }
        : {}),
    }
  })
  if (data.cursor !== null && (typeof data.cursor !== 'string' || !data.cursor)) {
    throw new Error('cache keys data.cursor is invalid')
  }
  const complete = requireBoolean(data.complete, 'cache keys data.complete')
  if (!complete && data.cursor === null) {
    throw new Error('incomplete cache keys response must include a cursor')
  }
  if (
    typeof data.count !== 'number' ||
    !Number.isSafeInteger(data.count) ||
    data.count !== keys.length
  ) {
    throw new Error('cache keys data.count does not match keys')
  }
  if (data.prefix !== expectedPrefix) {
    throw new Error('cache keys data.prefix does not match the request')
  }
  return {
    success: true,
    data: {
      keys,
      cursor: data.cursor,
      complete,
      count: data.count,
      prefix: expectedPrefix,
    },
  }
}

export const getCacheConfig = async (
  expectedMode?: NetworkEnvironment
): Promise<AdminCacheConfigResponse> => {
  const response = await adminApiClient.get<ApiResponse<unknown>>(
    '/admin/cache/config'
  )
  return requireCacheConfig(response?.data?.data, expectedMode)
}

export const getCacheKeys = async (params: {
  prefix: string
  limit: number
  cursor?: string
}): Promise<CacheKeyListResponse> => {
  if (!Number.isSafeInteger(params.limit) || params.limit < 1 || params.limit > 1000) {
    throw new Error('Cache key limit must be an integer between 1 and 1000')
  }
  const searchParams = new URLSearchParams({
    prefix: params.prefix,
    limit: params.limit.toString(),
  })
  if (params.cursor) {
    searchParams.set('cursor', params.cursor)
  }
  const response = await adminApiClient.get<unknown>(
    `/admin/cache/keys?${searchParams.toString()}`
  )
  return requireCacheKeyList(response.data, params.prefix)
}

export const getCacheDebugInfo = async (
  expectedMode?: NetworkEnvironment
): Promise<AdminCacheDebugResponse> => {
  const response = await adminApiClient.get<ApiResponse<unknown>>(
    '/admin/cache/debug'
  )
  return requireCacheDebugInfo(response?.data?.data, expectedMode)
}

export const CACHE_TOOL_QUERY_POLICY = {
  retry: false,
  staleTime: 0,
  gcTime: 0,
  refetchInterval: false,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const
